import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import GoogleSignInButton, { isGoogleSignInEnabled } from '../components/GoogleSignInButton';
import Monogram from '../assets/monogram.svg';

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(localStorage.getItem('loginError') || '');
  const { login, loginWithGoogle } = useAuth();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  // Both sign-in paths land here so a Google failure surfaces in the same place, and
  // with the same persistence, as a wrong password.
  const finishLogin = (result, fallbackError) => {
    setIsLoading(false);

    if (result && result.success) {
      navigate('/');
      return;
    }
    // Display error message directly in component as fallback
    setErrorMessage(result?.error || fallbackError);
    localStorage.setItem('loginError', result?.error || fallbackError); // Store in localStorage
  };

  const startLogin = () => {
    setIsLoading(true);
    setErrorMessage(''); // Clear previous errors
    localStorage.removeItem('loginError'); // Clear from localStorage
  };

  const onSubmit = async (data) => {
    startLogin();
    finishLogin(await login(data.username, data.password), 'Login failed');
  };

  const onGoogleCredential = async (credential) => {
    startLogin();
    finishLogin(await loginWithGoogle(credential), 'Google sign-in failed');
  };

  const clearError = () => {
    setErrorMessage('');
    localStorage.removeItem('loginError');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-primary-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="mx-auto h-16 w-16 flex items-center justify-center">
            <img src={Monogram} alt="ByteCorp Monogram" className="mx-auto mb-6 h-20 w-20 object-contain drop-shadow" />
          </div>
          <h2 className="mt-6 text-3xl font-bold text-gray-900">
            ByteCorp HR Portal
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Sign in to your account to continue
          </p>
        </div>

        <div className="bg-white py-8 px-6 shadow-soft rounded-xl space-y-6">
          {errorMessage && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3 flex-1">
                  <p data-testid="login-error" className="text-sm font-medium text-red-800">{errorMessage}</p>
                </div>
                <div className="ml-auto pl-3">
                  <button
                    type="button"
                    onClick={clearError}
                    className="inline-flex bg-red-50 rounded-md p-1.5 text-red-500 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-600"
                  >
                    <span className="sr-only">Dismiss</span>
                    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          )}

          <GoogleSignInButton onCredential={onGoogleCredential} />

          {isGoogleSignInEnabled && (
            <div className="relative">
              <div className="absolute inset-0 flex items-center" aria-hidden="true">
                <div className="w-full border-t border-gray-200" />
              </div>
              <div className="relative flex justify-center">
                <span className="bg-white px-3 text-xs uppercase tracking-wide text-gray-400">or</span>
              </div>
            </div>
          )}

          <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700">
                Username
              </label>
              <div className="mt-1">
                <input
                  id="username"
                  type="text"
                  data-testid="login-username"
                  {...register('username', { required: 'Username is required' })}
                  onChange={(e) => {
                    if (errorMessage) {
                      clearError();
                    }
                    register('username', { required: 'Username is required' }).onChange(e);
                  }}
                  className={`appearance-none relative block w-full px-3 py-2 border ${
                    errors.username ? 'border-red-300' : 'border-gray-300'
                  } placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm`}
                  placeholder="Enter your username"
                />
                {errors.username && (
                  <p className="mt-1 text-sm text-red-600">{errors.username.message}</p>
                )}
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <div className="mt-1 relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  data-testid="login-password"
                  {...register('password', { required: 'Password is required' })}
                  onChange={(e) => {
                    if (errorMessage) {
                      clearError();
                    }
                    register('password', { required: 'Password is required' }).onChange(e);
                  }}
                  className={`appearance-none relative block w-full px-3 py-2 pr-10 border ${
                    errors.password ? 'border-red-300' : 'border-gray-300'
                  } placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm`}
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  data-testid="login-password-toggle"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400" />
                  )}
                </button>
                {errors.password && (
                  <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
                )}
              </div>
            </div>

            <div>
              <button
                type="submit"
                data-testid="login-submit"
                onClick={handleSubmit(onSubmit)}
                disabled={isLoading}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isLoading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                ) : (
                  'Sign in'
                )}
              </button>
            </div>
          </form>
        </div>

        <div className="text-center space-y-2">
          <p className="text-xs text-gray-500">
            <Link to="/terms" className="hover:text-gray-700 hover:underline">Terms of Service</Link>
            <span className="mx-2 text-gray-300">|</span>
            <Link to="/privacy" className="hover:text-gray-700 hover:underline">Privacy Policy</Link>
          </p>
          <p className="text-xs text-gray-500">
            © {new Date().getFullYear()} ByteCorp. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login; 